'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { cardVariants, microHoverVariants } from '@/lib/animations';

interface StatsCardProps {
  title: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
  color?: 'blue' | 'green' | 'yellow' | 'red';
  translations?: Record<string, string>;
  index?: number;
}

export default function StatsCard({
  title,
  value,
  icon: Icon,
  color = 'blue',
  index = 0,
}: StatsCardProps) {

  const colorConfig = {
    blue: {
      iconBg: 'bg-gradient-to-br from-blue-500 to-blue-600',
      iconText: 'text-white',
      valueBg: 'bg-gradient-to-br from-blue-50 to-blue-100/50',
      valueText: 'text-blue-700',
      border: 'border-blue-200/50',
    },
    green: {
      iconBg: 'bg-gradient-to-br from-green-500 to-green-600',
      iconText: 'text-white',
      valueBg: 'bg-gradient-to-br from-green-50 to-green-100/50',
      valueText: 'text-green-700',
      border: 'border-green-200/50',
    },
    yellow: {
      iconBg: 'bg-gradient-to-br from-yellow-500 to-yellow-600',
      iconText: 'text-white',
      valueBg: 'bg-gradient-to-br from-yellow-50 to-yellow-100/50',
      valueText: 'text-yellow-700',
      border: 'border-yellow-200/50',
    },
    red: {
      iconBg: 'bg-gradient-to-br from-red-500 to-red-600',
      iconText: 'text-white',
      valueBg: 'bg-gradient-to-br from-red-50 to-red-100/50',
      valueText: 'text-red-700',
      border: 'border-red-200/50',
    },
  };

  const config = colorConfig[color];

  return (
    <motion.div 
      className="bg-white rounded-lg shadow-tour-card border border-gray-100 p-6 group hover:-translate-y-1 group-hover:shadow-2xl transition-all duration-300 transition-shadow duration-500 relative overflow-hidden"
      role="region"
      aria-labelledby={`stats-card-title-${index}`}
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      custom={index}
      whileHover="hover"
    >
      {/* Subtle background gradient */}
      <div className={`absolute inset-0 ${config.valueBg} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
      
      <div className="relative flex items-center justify-between">
        <div className="flex-1">
          <p id={`stats-card-title-${index}`} className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{title}</p>
          <p className={`text-4xl font-bold ${config.valueText} transition-colors duration-300`}>
            {value}
          </p>
        </div>
        <motion.div 
          className={`rounded-xl p-4 ${config.iconBg} shadow-lg group-hover:scale-110 transition-transform duration-300`} 
          aria-hidden="true"
          variants={microHoverVariants}
          whileHover="hover"
        >
          <Icon className={`h-7 w-7 ${config.iconText}`} />
        </motion.div>
      </div>
    </motion.div>
  );
}

